import { useState, useEffect } from 'react';

export interface ViewerIdHook {
  viewerId: string;
  refreshViewerId: () => void;
  clearViewerId: () => void;
}

const VIEWER_ID_KEY = 'marlo-viewer-id';

// Generate a unique viewer ID (moved outside to avoid hoisting issues)
const generateViewerId = (): string => {
  return 'viewer-' + Math.random().toString(36).slice(2) + '-' + Date.now();
};

export default function useViewerId(): ViewerIdHook {
  const [viewerId, setViewerId] = useState<string>(() => {
    // Initialize from localStorage on mount
    const stored = localStorage.getItem(VIEWER_ID_KEY);
    if (stored) return stored;
    
    // Generate new ID if none exists
    const newId = generateViewerId();
    localStorage.setItem(VIEWER_ID_KEY, newId);
    return newId;
  });

  // Refresh viewer ID (generate new one)
  const refreshViewerId = (): void => {
    const newId = generateViewerId();
    localStorage.setItem(VIEWER_ID_KEY, newId);
    setViewerId(newId);
  };

  // Clear viewer ID from storage
  const clearViewerId = (): void => {
    localStorage.removeItem(VIEWER_ID_KEY);
    setViewerId('');
  };

  // Sync with localStorage changes (in case other components modify it)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === VIEWER_ID_KEY && e.newValue !== viewerId) {
        setViewerId(e.newValue || '');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [viewerId]);

  return {
    viewerId,
    refreshViewerId,
    clearViewerId
  };
}
